import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomQuery } from '@hooks/core/useCustomQuery';
import { SubscriptionPopupDetail } from '@components/popups/SubscriptionPopup/SubscriptionPopup.types';
import { TrialMonthlyRebillPopupDetail } from '@components/popups/TrialMonthlyRebillPopup/TrialMonthlyRebillPopup.types';
import { RescuePopupDetail } from '@components/popups/RescuePopup/RescuePopup.types';
import { AnnualPlanPopupDetail } from '@components/popups/AnnualPlanPopup/AnnualPlanPopup.types';
import { WordsPricesPopupDetail } from '@components/popups/WordsPricesPopup/WordsPricesPopup.types';
import { PayAsYouGoPopupDetail } from '@components/popups/PayAsYouGoPopup/PayAsYouGoPopup.types';
import { QueryKeys } from '@constants/queryKeys';

const POPUPS_QUERY_KEY = [QueryKeys.POPUPS];

export type PopupDataMap = {
  SubscriptionPopup: SubscriptionPopupDetail;
  TrialMonthlyRebillPopup: TrialMonthlyRebillPopupDetail;
  RescuePopup: RescuePopupDetail;
  AnnualPlanPopup: AnnualPlanPopupDetail;
  WordsPricesPopup: WordsPricesPopupDetail;
  PayAsYouGoPopup: PayAsYouGoPopupDetail;
};

export type PopupName = keyof PopupDataMap;

type PopupsState = {
  [K in PopupName]?: { isOpen: boolean; data?: PopupDataMap[K] };
};

/** Popups state shared through the query cache — rendered by `Popups`. */
export const usePopups = () => {
  const queryClient = useQueryClient();

  const { data: popups = {} } = useCustomQuery<PopupsState>(
    POPUPS_QUERY_KEY,
    () => queryClient.getQueryData<PopupsState>(POPUPS_QUERY_KEY) ?? {},
    { initialData: {}, staleTime: Infinity }
  );

  const openPopup = useCallback(
    <T extends PopupName>(name: T, data?: PopupDataMap[T]) => {
      queryClient.setQueryData<PopupsState>(POPUPS_QUERY_KEY, (prevData) => ({
        ...prevData,
        [name]: { isOpen: true, data },
      }));
    },
    [queryClient]
  );

  const closePopup = useCallback(
    (name: PopupName) => {
      queryClient.setQueryData<PopupsState>(POPUPS_QUERY_KEY, (prevData) => ({
        ...prevData,
        [name]: { isOpen: false },
      }));
    },
    [queryClient]
  );

  return { popups, openPopup, closePopup };
};
